import {
    gql,
    useQuery
} from "@apollo/client";
import {
  FindAllPartnerExchangeRatesQueryQuery,
  QueryFindAllPartnerExchangeRatesArgs
} from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findAllPartnerExchangeRatesQuery = gql`
  query FindAllPartnerExchangeRatesQuery(
    $page: Int!
    $pageSize: Int!
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllPartnerExchangeRates(
      page: $page
      pageSize: $pageSize
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        communityId
        communityName
        communityAlias
        rate
        createdAt
        updatedAt
        adminName
        adminLevel
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

type PartnerExchangeRateListItem = NonNullable<
  NonNullable<
    NonNullable<FindAllPartnerExchangeRatesQueryQuery["findAllPartnerExchangeRates"]>["data"]
  >[number]
>;

export interface WithFindAllPartnerExchangeRates {
  data: PartnerExchangeRateListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMorePartnerExchangeRates: boolean;
}

interface Props {
  variables: QueryFindAllPartnerExchangeRatesArgs;
  children: (data: WithFindAllPartnerExchangeRates) => React.ReactElement | null;
}

export const FindAllPartnerExchangeRatesController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllPartnerExchangeRatesQueryQuery,
    QueryFindAllPartnerExchangeRatesArgs
  >(findAllPartnerExchangeRatesQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const partnerExchangeRates = (data?.findAllPartnerExchangeRates?.data || [])
    .filter((rate): rate is PartnerExchangeRateListItem => Boolean(rate));
  const page = data?.findAllPartnerExchangeRates?.page || variables.page;
  const pageSize = data?.findAllPartnerExchangeRates?.pageSize || variables.pageSize;
  const total = data?.findAllPartnerExchangeRates?.total || 0;
  const totalPages = data?.findAllPartnerExchangeRates?.totalPages || 0;
  const hasMorePartnerExchangeRates = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMorePartnerExchangeRates) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllPartnerExchangeRates) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllPartnerExchangeRates: {
            ...fetchMoreResult?.findAllPartnerExchangeRates,
            data: [
              ...(prev?.findAllPartnerExchangeRates?.data || []),
              ...(fetchMoreResult?.findAllPartnerExchangeRates?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMorePartnerExchangeRates]);

  return children({
    data: partnerExchangeRates,
    loading,
    loadMore,
    hasMorePartnerExchangeRates,
    page,
    pageSize,
    total,
    totalPages,
  });
};
